<template>
  <form novalidate data-testid="profile-form-fields" @submit="onSubmit">
    <JRow spaceMd="12" spaceLg="12" spaceXl="12" class="mb-12 gap-y-16">
      <JCol md="6" lg="6" xl="6">
        <JTextInput
          v-model="firstName.value.value"
          :error="firstName.errorMessage.value"
          type="text"
          :label="$t('profile.firstName.label')"
          required
          :placeholder="$t('profile.firstName.placeholder')"
          data-testid="profile-form-fields-first-name"
        />
      </JCol>
      <JCol md="6" lg="6" xl="6">
        <JTextInput
          v-model="middleName.value.value"
          :error="middleName.errorMessage.value"
          type="text"
          :label="$t('profile.middleName.label')"
          :placeholder="$t('profile.middleName.placeholder')"
          data-testid="profile-form-fields-middle-name"
        />
      </JCol>
      <JCol md="6" lg="6" xl="6">
        <JTextInput
          v-model="lastName.value.value"
          :error="lastName.errorMessage.value"
          type="text"
          :label="$t('profile.lastName.label')"
          required
          :placeholder="$t('profile.lastName.placeholder')"
          data-testid="profile-form-fields-last-name"
        />
      </JCol>
      <JCol md="6" lg="6" xl="6">
        <JSelect
          v-model="sexOnPassport.value.value"
          :error="sexOnPassport.errorMessage.value"
          required
          :options="sexList"
          :label="t('profile.sexOnPassport.label')"
          :placeholder="t('profile.sexOnPassport.placeholder')"
          optionKey="key"
          optionValue="value"
          data-testid="profile-form-fields-sex-on-passport"
        />
      </JCol>
      <JCol md="6" lg="6" xl="6">
        <JSelect
          v-model="gender.value.value"
          :error="gender.errorMessage.value"
          :options="genderList"
          :label="$t('profile.gender.label')"
          :placeholder="$t('profile.gender.placeholder')"
          optionKey="key"
          optionValue="value"
          data-testid="profile-form-fields-gender"
        />
      </JCol>
      <JCol md="6" lg="6" xl="6">
        <JTelInput
          v-model="phone.value.value"
          :error="phone.errorMessage.value"
          :label="$t('profile.telephone.label')"
          :placeholder="$t('profile.telephone.placeholder')"
          name="telephone"
          :disabled="!!form?.phone"
          data-testid="profile-form-fields-phone"
        />
      </JCol>
      <JCol md="6" lg="6" xl="6">
        <JTextInput
          v-model="email.value.value"
          :error="email.errorMessage.value"
          type="email"
          :label="$t('profile.email.label')"
          :placeholder="$t('profile.email.placeholder')"
          :disabled="!!authStore.user.email"
          data-testid="profile-form-fields-email"
        />
      </JCol>
      <JCol md="6" lg="6" xl="6">
        <JDateTimePicker
          v-model="birthDate.value.value"
          :error="birthDate.errorMessage.value"
          type="date"
          required
          :maxDate="maxDOB"
          :startDate="maxDOB"
          :label="$t('profile.birthdate.label')"
          :placeholder="$t('profile.birthdate.placeholder')"
          data-testid="profile-form-fields-birth-date"
        />
      </JCol>
      <JCol md="6" lg="6" xl="6">
        <AirportSelectInput
          v-model="homeAirport.value.value"
          :error="homeAirport.errorMessage.value"
          required
          :label="$t('profile.homeAirport.label')"
          :placeholder="$t('profile.homeAirport.placeholder')"
          :excludedTypes="['S']"
          data-testid="profile-form-fields-home-airport"
        />
      </JCol>
      <JCol md="6" lg="6" xl="6">
        <JTextInput
          v-model="passport.value.value"
          :error="passport.errorMessage.value"
          :label="$t('profile.passport.label')"
          :placeholder="$t('profile.passport.placeholder')"
          data-testid="profile-form-fields-passport"
        />
      </JCol>
      <JCol md="6" lg="6" xl="6">
        <CountrySelectInput
          v-model="country.value.value"
          :error="country.errorMessage.value"
          data-testid="profile-form-fields-country"
        />
      </JCol>
      <JCol md="6" lg="6" xl="6">
        <JDateTimePicker
          v-model="issueDate.value.value"
          :error="issueDate.errorMessage.value"
          :maxDate="new Date()"
          :startDate="new Date()"
          type="date"
          :label="$t('profile.issueDate.label')"
          :placeholder="$t('profile.issueDate.placeholder')"
          data-testid="profile-form-fields-issue-date"
        />
      </JCol>
      <JCol md="6" lg="6" xl="6">
        <JDateTimePicker
          v-model="expireDate.value.value"
          :error="expireDate.errorMessage.value"
          type="date"
          :startDate="new Date()"
          :label="t('profile.expiryDate.label')"
          :placeholder="t('profile.expiryDate.placeholder')"
          data-testid="profile-form-fields-expiry-date"
        />
      </JCol>
    </JRow>

    <div class="flex md:justify-end">
      <JButton
        size="md"
        :block="isMobile"
        :isLoading="isLoading"
        :disabled="!isChanged || isLoading"
        type="submit"
        data-testid="profile-form-fields-save-button"
      >
        {{ t('words.save') }}
      </JButton>
    </div>
  </form>
</template>

<script setup lang="ts">
import isEqual from 'lodash/isEqual'
import { object, string } from 'yup'
import { useField, useForm } from 'vee-validate'
import { useI18n } from 'vue-i18n'
import { computed, inject, unref, watch } from 'vue'
import {
  JButton,
  JCol,
  JDateTimePicker,
  JRow,
  JSelect,
  JTelInput,
  JTextInput,
} from '@journey/ui-kit'
import type { Form, Props } from './MyProfileFormFields.types'
import { GENDERS, REGEX, SEX } from '@/modules/Core/constants'
import { compareDates, dateYearsAgo, yupSchema } from '@/modules/Core/utils'
import AirportSelectInput from '@/modules/Profile/components/AirportSelectInput/AirportSelectInput.vue'
import useProfileStore from '@/modules/Profile/store'
import {
  MY_PROFILE_FORM_INJECTION_KEY,
  updateProfileSectionSharedStateInjectionKey,
} from '@/modules/Profile/constants'
import useDeviceType from '@/modules/Core/composables/useDevicePlatform'
import useAuthStore from '@/modules/Auth/store'
import CountrySelectInput from '@/modules/Core/components/shared/CountrySelectInput/CountrySelectInput.vue'

const props = defineProps<Props>()

const { isMobile } = useDeviceType()

const { t } = useI18n()

const genderList = Object.entries(GENDERS).map(([key, value]) => ({
  key,
  value: t(value),
}))

const sexList = Object.entries(SEX).map(([key, value]) => ({
  key,
  value: t(value),
}))

const validationSchema = object({
  firstName: string()
    .matches(REGEX.ONLY_LETTERS, t('profile.firstName.validation.lettersOnly'))
    .min(3, t('validation.firstName.minLength'))
    .required(t('profile.firstName.validation.required')),
  middleName: string()
    .matches(REGEX.ONLY_LETTERS, t('profile.middleName.validation.lettersOnly'))
    .optional()
    .nullable()
    .transform((value) => value || null),
  lastName: string()
    .matches(REGEX.ONLY_LETTERS, t('profile.lastName.validation.lettersOnly'))
    .min(3, t('validation.lastName.minLength'))
    .required(t('profile.lastName.validation.required')),
  gender: string()
    .oneOf(
      genderList.map((item) => item.key),
      t('profile.gender.validation.valid')
    )
    .optional()
    .nullable(),
  sexOnPassport: string()
    .oneOf(
      sexList.map((item) => item.key),
      t('profile.sexOnPassport.validation.valid')
    )
    .required(t('profile.sexOnPassport.validation.required')),
  phone: yupSchema
    .phoneSchema(false)
    .optional()
    .nullable()
    .transform((value) => value || null),
  email: string()
    .matches(REGEX.EMAIL, t('profile.email.validation.valid'))
    .optional()
    .nullable()
    .transform((value) => value || null),
  birthDate: string()
    .matches(REGEX.DATE, t('profile.birthdate.validation.valid'))
    .required(t('profile.birthdate.validation.required')),
  homeAirport: string().required(t('profile.homeAirport.validation.required')),
  passport: string()
    .matches(REGEX.PASSPORT, t('profile.passport.validation.valid'))
    .optional()
    .nullable()
    .transform((value) => value || null),
  country: string().optional().nullable(),
  issueDate: string()
    .when('birthDate', (birthDate, schema) => {
      if (String(birthDate)) {
        return schema.test({
          name: 'birthDate',
          exclusive: true,
          message: t('profile.issueDate.validation.greaterThanBirthDate'),
          test: (value) =>
            value ? compareDates(String(value), String(birthDate)) === 1 : true,
        })
      }
      return schema
    })
    .matches(REGEX.DATE, t('profile.issueDate.validation.valid'))
    .optional()
    .nullable()
    .transform((value) => value || null),
  expiryDate: string()
    .test({
      name: 'checkExpiryDate',
      message: t('profile.pastExpiryDate'),
      test: (value) => {
        if (!value) {
          return true
        }

        const [day, month, year] = value.split('/')
        const iso = `${year}-${month}-${day}`
        const parsedDate = new Date(iso).getTime()

        const today = new Date()
        today.setHours(0, 0, 0, 0)

        if (parsedDate >= today.getTime()) {
          return true
        }
        return false
      },
    })
    .when('issueDate', (issueDate, schema) => {
      if (String(issueDate)) {
        return schema.test({
          name: 'expiryDate',
          exclusive: true,
          message: t('profile.expiryDate.validation.valid'),
          test: (value) =>
            value ? compareDates(String(value), String(issueDate)) === 1 : true,
        })
      }
      return schema
    })
    .matches(REGEX.DATE, t('profile.expiryDate.validation.valid'))
    .optional()
    .nullable()
    .transform((value) => value || null),
})

const authStore = useAuthStore()

const { handleSubmit, values, meta } = useForm<Form>({
  validationSchema,
  initialValues: {
    ...props.form,
    email: props.form?.email || authStore.user.email,
    phone: props.form?.phone || authStore.user.phone,
  },
  keepValuesOnUnmount: true,
})

const firstName = useField<Form['firstName']>('firstName')
const middleName = useField<Form['middleName']>('middleName')
const lastName = useField<Form['lastName']>('lastName')
const gender = useField<Form['gender']>('gender')
const phone = useField<Form['phone']>('phone')
const email = useField<Form['email']>('email')
const birthDate = useField<Form['birthDate']>('birthDate')
const homeAirport = useField<Form['homeAirport']>('homeAirport')
const passport = useField<Form['passport']>('passport')
const country = useField<Form['country']>('country')
const issueDate = useField<Form['issueDate']>('issueDate')
const expireDate = useField<Form['expiryDate']>('expiryDate')
const sexOnPassport = useField<Form['sexOnPassport']>('sexOnPassport')

const maxDOB = dateYearsAgo(16)

const updateProfileSectionSharedState = inject(
  updateProfileSectionSharedStateInjectionKey
)

// DISABLED
const isChanged = computed(() => !isEqual(props.form, values))

watch([isChanged, () => meta.value.valid], ([changed, valid]) => {
  if (!updateProfileSectionSharedState) {
    return
  }

  updateProfileSectionSharedState.setTouched(
    MY_PROFILE_FORM_INJECTION_KEY,
    valid && changed
  )
})

// SUBMISSION
const onSubmit = handleSubmit(async (values) => {
  if (!isChanged.value) {
    return
  }

  if (!updateProfileSectionSharedState) {
    return
  }

  await updateProfileSectionSharedState.items[
    MY_PROFILE_FORM_INJECTION_KEY
  ].submit(values)

  updateProfileSectionSharedState.setTouched(
    MY_PROFILE_FORM_INJECTION_KEY,
    false
  )
})

const profileStore = useProfileStore()

watch(values, (value) => {
  profileStore.setConfirmationPayload(isChanged.value ? value : null)
})

// LOADING
const isLoading = computed(() => {
  if (!updateProfileSectionSharedState) {
    return
  }

  return unref(
    updateProfileSectionSharedState?.items[MY_PROFILE_FORM_INJECTION_KEY]
      .isLoading
  )
})
</script>
